// Утилита для работы с localStorage
// Сохраняем данные для удобства клиента и оффлайн доступа

const STORAGE_KEYS = {
  USER_TOKEN: 'fight_user_token',
  CHARACTER_DATA: 'fight_character_data',
  GAME_SETTINGS: 'fight_game_settings',
  LAST_SERVER: 'fight_last_server',
} as const;

export class LocalStorage {
  // Сохранение данных
  static set<T>(key: keyof typeof STORAGE_KEYS, value: T): void {
    try {
      const serialized = JSON.stringify(value);
      localStorage.setItem(STORAGE_KEYS[key], serialized);
    } catch (error) {
      console.error(`Failed to save ${key} to localStorage:`, error);
    }
  }

  // Получение данных
  static get<T>(key: keyof typeof STORAGE_KEYS): T | null {
    try {
      const item = localStorage.getItem(STORAGE_KEYS[key]);
      if (!item) return null;
      return JSON.parse(item) as T;
    } catch (error) {
      console.error(`Failed to get ${key} from localStorage:`, error);
      return null;
    }
  }

  // Удаление данных
  static remove(key: keyof typeof STORAGE_KEYS): void {
    try {
      localStorage.removeItem(STORAGE_KEYS[key]);
    } catch (error) {
      console.error(`Failed to remove ${key} from localStorage:`, error);
    }
  }

  // Очистка всех данных игры
  static clear(): void {
    Object.values(STORAGE_KEYS).forEach(key => {
      localStorage.removeItem(key);
    });
  }

  // Проверка наличия данных
  static has(key: keyof typeof STORAGE_KEYS): boolean {
    return localStorage.getItem(STORAGE_KEYS[key]) !== null;
  }
}

// Типы для хранимых данных
export interface StoredCharacter {
  id: string;
  nickname: string;
  class_id?: string;
  created_at: string;
  // Добавим больше полей по мере развития игры
}

export interface GameSettings {
  soundEnabled: boolean;
  musicVolume: number;
  effectsVolume: number;
  graphics: 'low' | 'medium' | 'high';
  language: 'ru' | 'en';
}

// Значения по умолчанию
export const DEFAULT_GAME_SETTINGS: GameSettings = {
  soundEnabled: true,
  musicVolume: 0.7,
  effectsVolume: 0.8,
  graphics: 'medium',
  language: 'ru'
};

// Хелперы для работы с конкретными данными
export const StorageHelpers = {
  // Токен авторизации
  saveToken(token: string): void {
    LocalStorage.set('USER_TOKEN', token);
  },
  
  getToken(): string | null {
    return LocalStorage.get<string>('USER_TOKEN');
  },
  
  clearToken(): void {
    LocalStorage.remove('USER_TOKEN');
  },

  // Персонаж
  saveCharacter(character: StoredCharacter): void {
    LocalStorage.set('CHARACTER_DATA', character);
  },
  
  getCharacter(): StoredCharacter | null {
    return LocalStorage.get<StoredCharacter>('CHARACTER_DATA');
  },
  
  clearCharacter(): void {
    LocalStorage.remove('CHARACTER_DATA');
  },

  // Настройки
  saveSettings(settings: Partial<GameSettings>): void {
    const current = this.getSettings();
    LocalStorage.set('GAME_SETTINGS', { ...current, ...settings });
  },
  
  getSettings(): GameSettings {
    return LocalStorage.get<GameSettings>('GAME_SETTINGS') || DEFAULT_GAME_SETTINGS;
  },

  // Последний сервер (для переподключения)
  saveLastServer(url: string): void {
    LocalStorage.set('LAST_SERVER', url);
  },
  
  getLastServer(): string | null {
    return LocalStorage.get<string>('LAST_SERVER');
  }
};
